/**
 * Helpers for purchase custom-field values that need stable draft persistence:
 * - boolean checkbox (Aceite das Diretrizes → 'true'/'false')
 * - option radios/checks (Sim/Não → texto da opção)
 */

type CheckLikeField = {
  field_type: string;
  allow_multiple?: boolean | null;
  options?: string | null;
};

export const parseCheckFieldOptions = (optionsRaw?: string | null): string[] => {
  if (!optionsRaw) return [];
  const raw = String(optionsRaw).trim();
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((opt) => String(opt).trim()).filter((opt) => opt.length > 0);
    }
    if (parsed !== null && parsed !== undefined && String(parsed).trim() !== '') {
      return [String(parsed).trim()];
    }
  } catch {
    // fallback: lista separada por vírgula ou quebra de linha (ex.: "Sim\nNão")
  }

  return raw
    .split(/[,\n]/)
    .map((opt) => opt.trim())
    .filter((opt) => opt.length > 0);
};

/** Checkbox único sem opções (valor booleano true/false). */
export const isBooleanSingleCheckField = (field: CheckLikeField): boolean =>
  field.field_type === 'check' && !field.allow_multiple && parseCheckFieldOptions(field.options).length === 0;

export const isSingleCheckFieldChecked = (value: unknown): boolean =>
  value === true || value === 'true' || value === 1 || value === '1';

export const serializeSingleCheckFieldValue = (value: unknown): 'true' | 'false' =>
  isSingleCheckFieldChecked(value) ? 'true' : 'false';

/** True when the user (or draft) already set a boolean single-check value. */
export const hasSingleCheckFieldValue = (value: unknown): boolean =>
  value === true ||
  value === false ||
  value === 'true' ||
  value === 'false' ||
  value === 1 ||
  value === 0 ||
  value === '1' ||
  value === '0';

/** Qualquer campo check com valor escolhido (boolean ou opção tipo Sim/Não). */
export const hasCheckFieldPersistedValue = (field: CheckLikeField, value: unknown): boolean => {
  if (field.field_type !== 'check') return false;

  if (field.allow_multiple) {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string' && value.trim()) {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.length > 0 : String(parsed).trim() !== '';
      } catch {
        return true;
      }
    }
    return false;
  }

  if (isBooleanSingleCheckField(field)) {
    return hasSingleCheckFieldValue(value);
  }

  // Radio/check com opções (Sim/Não, etc.)
  return value !== undefined && value !== null && String(value).trim() !== '';
};

export const serializeCheckFieldValue = (field: CheckLikeField, value: unknown): string | null => {
  if (field.field_type !== 'check') return null;

  if (field.allow_multiple) {
    if (Array.isArray(value)) {
      return value.length > 0 ? JSON.stringify(value.map((v) => String(v))) : null;
    }
    const asString = String(value ?? '').trim();
    return asString ? asString : null;
  }

  if (isBooleanSingleCheckField(field)) {
    if (!hasSingleCheckFieldValue(value)) return null;
    return serializeSingleCheckFieldValue(value);
  }

  const optionValue = String(value ?? '').trim();
  return optionValue || null;
};

/**
 * Hydrate API text_value into form state.
 * JSON booleans from "true"/"false" become strings so the checkbox UI stays consistent.
 */
export const hydrateCustomFieldTextValue = (textValue: string): unknown => {
  try {
    const parsed = JSON.parse(textValue);
    if (typeof parsed === 'boolean') {
      return parsed ? 'true' : 'false';
    }
    return parsed;
  } catch {
    return textValue;
  }
};
